import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Layout } from "../../components/Layout";
import { Button } from "../../components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { useLang } from "../../contexts/LangContext";
import { billingService } from "../../services/billingService";
import { formatMoney } from "../../lib/api";
import {
  CalendarClock, CreditCard, Package2, ShieldCheck, ArrowRight,
  XCircle, Receipt, RefreshCw, Layers, AlertTriangle, Hourglass, Infinity, Play,
  Mail, Sparkles, ShoppingCart,
} from "lucide-react";
import { useSupportInfo } from "../../hooks/useSupportInfo";
import { MyGrantsCard } from "./MyGrantsCard";
import { PageHeader } from "../../components/PageHeader";
import { LifecycleBanner } from "./LifecycleBanner";
import { ProrationCheckoutModal } from "./ProrationCheckoutModal";
import PlanEntitlementsCard from "./PlanEntitlementsCard";
import { DemoPendingOfferBanner } from "./DemoPendingOfferBanner";

// iter-169v — feature-driven usage map. Replaces the legacy
// `ai_messages_allotment/used` global counter. Add new metered features
// here as they get a `usage_limit` in the entitlements catalogue.
const FEATURE_LABELS = {
  image_extractions: { es: "Recibos IA", en: "AI Receipts", pt: "Recibos IA" },
  advisor_chat:      { es: "Asistente IA",          en: "AI Advisor",        pt: "Assistente IA" },
};

function featureLabel(key, lang) {
  const m = FEATURE_LABELS[key];
  if (!m) return key;
  return m[lang] || m.es;
}

const STATUS_LABEL = {
  active:    { es: "Activo",     en: "Active",     pt: "Ativo",     cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pending:   { es: "Pendiente",  en: "Pending",    pt: "Pendente",  cls: "bg-amber-50 text-amber-700 border-amber-200" },
  cancelled: { es: "Cancelada",  en: "Cancelled",  pt: "Cancelada", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  approved:  { es: "Aprobado",   en: "Approved",   pt: "Aprovado",  cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  initiated: { es: "Iniciado",   en: "Initiated",  pt: "Iniciado",  cls: "bg-slate-50 text-slate-600 border-slate-200" },
  rejected:  { es: "Rechazado",  en: "Rejected",   pt: "Rejeitado", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  refunded:  { es: "Reembolsado",en: "Refunded",   pt: "Reembolsado", cls: "bg-slate-50 text-slate-600 border-slate-200" },
  // iter-168: lifecycle deferred states
  past_due:               { es: "Pago pendiente",         en: "Past due",            pt: "Pagamento pendente",  cls: "bg-rose-50 text-rose-700 border-rose-200" },
  paused:                 { es: "Pausada",                en: "Paused",              pt: "Pausada",             cls: "bg-slate-50 text-slate-600 border-slate-200" },
  pending_cancellation:   { es: "Cancelación programada", en: "Cancellation scheduled", pt: "Cancelamento agendado", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  pending_downgrade:      { es: "Cambio programado",      en: "Change scheduled",    pt: "Mudança agendada",    cls: "bg-slate-50 text-slate-700 border-slate-200" },
};

function StatusBadge({ status, lang }) {
  const k = STATUS_LABEL[status] || { es: status, en: status, pt: status, cls: "bg-slate-50 text-slate-600 border-slate-200" };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${k.cls}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {k[lang] || k.es}
    </span>
  );
}

function fmtDate(iso, lang) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(lang === "en" ? "en-US" : "es-AR", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch {
    return iso;
  }
}

function fmtDateTime(iso, lang) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(lang === "en" ? "en-US" : "es-AR", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function MyPlan() {
  const { lang } = useLang();
  const isEs = lang !== "en";
  const isPt = lang === "pt";
  const qc = useQueryClient();
  const [cancelOpen, setCancelOpen] = useState(false);

  const { data: me, isLoading: meLoading, refetch: refetchMe } = useQuery({
    queryKey: ["billing-me"], queryFn: () => billingService.me(),
  });
  const { data: history, isLoading: histLoading } = useQuery({
    queryKey: ["billing-my-history"], queryFn: () => billingService.myHistory(),
  });
  const { data: userPacks, isLoading: packsLoading } = useQuery({
    queryKey: ["billing-my-packs"], queryFn: () => billingService.myUserPacks(),
  });
  // Resolve secondary plan names by hitting the plan list and projecting names.
  const primaryLineage = me?.plan?.lineage_id || me?.composition?.primary_plan_lineage_id;
  const { data: secondariesResp } = useQuery({
    queryKey: ["billing-secondaries", primaryLineage],
    queryFn: () => billingService.secondariesForPrimary(primaryLineage),
    enabled: !!primaryLineage,
  });
  const secondaryName = (() => {
    const out = {};
    (secondariesResp?.secondaries || []).forEach((s) => { out[s.id] = s.name; });
    return out;
  })();

  const cancel = useMutation({
    mutationFn: () => billingService.compositionCancel(),
    onSuccess: (data) => {
      // iter-168: backend now schedules cancellation to end of period
      // (not immediate). Reflect that in the success toast so the user knows
      // they still have access for the rest of the cycle + can revert.
      const when = data?.effective_at
        ? new Date(data.effective_at).toLocaleDateString(isEs ? "es-AR" : "en-US",
            { day: "2-digit", month: "long" })
        : null;
      toast.success(
        isEs
          ? when
            ? `Cancelación programada. Mantienes acceso hasta el ${when}.`
            : "Cancelación programada al fin del período."
          : when
            ? `Cancellation scheduled. You keep access until ${when}.`
            : "Cancellation scheduled at period end.",
      );
      qc.invalidateQueries({ queryKey: ["billing-me"] });
      setCancelOpen(false);
    },
    onError: () => toast.error(isEs ? "No se pudo cancelar. Reintenta." : "Could not cancel. Retry."),
  });
  const cancelPending = useMutation({
    mutationFn: () => billingService.compositionCancelPending(),
    onSuccess: () => {
      toast.success(isEs ? "Compra pendiente cancelada." : "Pending purchase cancelled.");
      qc.invalidateQueries({ queryKey: ["billing-me"] });
    },
    onError: () => toast.error(isEs ? "No se pudo cancelar. Reintenta." : "Could not cancel. Retry."),
  });
  const cancelRequestMut = useMutation({
    mutationFn: (id) => billingService.cancelPackRequest(id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["billing-my-packs"] });
      const refunded = r?.units_refunded || 0;
      if (refunded > 0) {
        toast.success(
          isPt ? `Solicitação cancelada. ${refunded} unidade${refunded === 1 ? "" : "s"} devolvida${refunded === 1 ? "" : "s"}.`
          : isEs ? `Solicitud cancelada. Te devolvimos ${refunded} unidad${refunded === 1 ? "" : "es"}.`
          : `Request cancelled. ${refunded} unit${refunded === 1 ? "" : "s"} refunded.`,
        );
      } else {
        toast.success(isEs ? "Solicitud cancelada." : "Request cancelled.");
      }
    },
    onError: () => toast.error(isEs ? "No se pudo cancelar." : "Could not cancel."),
  });
  const consumeMut = useMutation({
    mutationFn: ({ id, units }) => billingService.consumeUserPack(id, units),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["billing-my-packs"] });
      if (r.fulfillment_mode === "manual") {
        toast.success(isPt ? "Solicitação enviada à equipe. Em breve te contatamos."
                    : isEs ? "Solicitud enviada al equipo. En breve te contactamos."
                    : "Request sent to the team. We'll reach out shortly.");
      } else {
        toast.success(isPt ? `Unidade usada. Restam ${r.units_remaining}.`
                    : isEs ? `Unidad usada. Quedan ${r.units_remaining}.`
                    : `Unit used. ${r.units_remaining} remaining.`);
      }
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail || "";
      if (detail === "not_enough_units") toast.error(isEs ? "No te quedan unidades suficientes." : "Not enough units.");
      else if (detail === "user_pack_expired") toast.error(isEs ? "El pack venció." : "Pack expired.");
      else if (detail === "user_pack_exhausted") toast.error(isEs ? "El pack ya está agotado." : "Pack already exhausted.");
      // iter-170k — manual fulfillment requires soporte to resolve the
      // previous request before the user can submit a new one.
      else if (detail === "consumption_request_pending") {
        toast.error(isPt
          ? "Já existe uma solicitação aberta. Aguarda a resposta do suporte."
          : isEs
          ? "Ya tienes una solicitud pendiente. Espera la respuesta de soporte para hacer otra."
          : "You already have an open request. Wait for support to respond before submitting another.");
      }
      else toast.error(isEs ? "No se pudo usar." : "Could not use.");
    },
  });

  const T = {
    title:           isPt ? "Meu plano" : isEs ? "Mi plan" : "My plan",
    subtitle:        isPt ? "Sua assinatura, packs e pagamentos em um só lugar."
                          : isEs ? "Tu suscripción, packs y pagos en un solo lugar."
                          : "Your subscription, packs and payments in one place.",
    status:          isPt ? "Status" : isEs ? "Estado" : "Status",
    plan:            isEs ? "Plan" : "Plan",
    addons:          isEs ? "Add-ons" : "Add-ons",
    nextRenewal:     isEs ? "Próxima renovación" : "Next renewal",
    daysLeft:        isEs ? "días restantes" : "days left",
    usageTitle:      isPt ? "Seu uso neste mês"
                          : isEs ? "Tu uso este mes"
                          : "Your usage this month",
    usageEmpty:      isPt ? "Esse plano não possui recursos limitados por uso."
                          : isEs ? "Este plan no tiene funciones con uso limitado."
                          : "This plan has no metered features.",
    packBalance:     isPt ? "Saldo do pack do assistente"
                          : isEs ? "Saldo de packs del asistente"
                          : "Assistant pack balance",
    packBalanceSub:  isPt ? "Mensagens IA disponíveis em packs (não expiram neste ciclo)."
                          : isEs ? "Mensajes del asistente disponibles desde packs (no expiran este ciclo)."
                          : "Assistant messages available from packs (don't expire this cycle).",
    buyMore:         isEs ? "Comprar pack" : "Buy pack",
    paymentsHist:    isEs ? "Historial de pagos" : "Payment history",
    noPayments:      isEs ? "Aún no hay pagos." : "No payments yet.",
    noPlan:          isEs ? "Aún no tienes plan activo" : "No active plan yet",
    noPlanSub:       isEs ? "Suscríbete para desbloquear el copiloto IA y todo el flujo de caja avanzado."
                          : "Subscribe to unlock the AI copilot and full advanced cash flow.",
    seePlans:        isEs ? "Ver planes" : "See plans",
    changePlan:      isEs ? "Cambiar plan" : "Change plan",
    buyPacks:        isEs ? "Comprar packs" : "Buy packs",
    cancelSub:       isEs ? "Cancelar suscripción" : "Cancel subscription",
    cancelPending:   isEs ? "Cancelar compra pendiente" : "Cancel pending checkout",
    confirmCancel:   isEs ? "¿Cancelar tu suscripción?" : "Cancel your subscription?",
    confirmCancelD:  isEs ? "Mantendrás el acceso hasta el fin del período actual. Podrás revertir esta cancelación cuando quieras antes de esa fecha."
                          : "You'll keep access until the end of the current period. You can undo this cancellation any time before that date.",
    keep:            isEs ? "Mantener" : "Keep",
    confirm:         isEs ? "Sí, cancelar al fin del período" : "Yes, cancel at period end",
    when:            isEs ? "Cuándo" : "When",
    concept:         isEs ? "Concepto" : "Concept",
    amount:          isEs ? "Monto" : "Amount",
    mpId:            "MP ID",
    type_sub:        isEs ? "Suscripción" : "Subscription",
    type_pack:       isEs ? "Pack" : "Pack",
    refresh:         isEs ? "Actualizar" : "Refresh",
    private:         isEs ? "Tus datos están encriptados y privados." : "Your data is encrypted and private.",
  };

  const comp = me?.composition;
  const plan = me?.plan;
  const sub = me?.subscription;
  const usage = me?.usage || {};
  const status = comp?.status || (sub?.status) || null;

  return (
    <Layout>
      <div className="space-y-8" data-testid="my-plan-page">
        <PageHeader
          testid="my-plan-header"
          eyebrow={isEs ? "Tu suscripción" : "Your subscription"}
          title={T.title}
          subtitle={T.subtitle}
          actions={
            <Button
              data-testid="my-plan-refresh"
              variant="outline"
              size="sm"
              onClick={() => { refetchMe(); qc.invalidateQueries({ queryKey: ["billing-my-history"] }); }}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" /> {T.refresh}
            </Button>
          }
        />

        {/* iter-205 — Pending demo offer (sticky-styled call to action). */}
        <DemoPendingOfferBanner lang={lang} />

        {/* iter-168: lifecycle state banner — surfaces past_due / pending_cancellation /
            pending_downgrade / pending_proration / paused with actionable CTAs. */}
        {comp ? <LifecycleBanner comp={comp} lang={lang} /> : null}

        {/* iter-168e: Proration checkout modal — auto-opens when there's a pending
            upgrade payment with a stored init_point. Closes when user dismisses
            or when /billing/me reflects the payment was approved. */}
        {comp ? (
          <ProrationCheckoutModal
            comp={comp}
            lang={lang}
            initPoint={comp.pending_proration_init_point}
            onClose={() => refetchMe()}
          />
        ) : null}

        {/* Plan status */}
        {meLoading ? (
          <div className="rounded-3xl border border-cream-300 bg-white p-10 text-center text-slate-400">…</div>
        ) : !comp && !plan ? (
          <EmptyPlan T={T} isEs={isEs} recentlyCancelled={me?.recently_cancelled} />
        ) : (
          <ActivePlan
            comp={comp} plan={plan} sub={sub} usage={usage} status={status}
            T={T} lang={lang}
            viaGrant={!!me?.via_grant}
            grantExpiresAt={me?.grant_expires_at}
            onChange={() => null}
            onBuy={() => null}
            onCancel={() => setCancelOpen(true)}
            onCancelPending={() => cancelPending.mutate()}
            cancelPendingPending={cancelPending.isPending}
            secondaryName={secondaryName}
          />
        )}

        {/* iter-169ze — Rich entitlements card: features + resources +
            services from the user's active plan/addons/packs/grants.
            Hidden when the user has no plan yet. */}
        {(comp || plan) && (
          <PlanEntitlementsCard entitlements={me?.entitlements} lang={lang} />
        )}

        {/* Active packs (consumables) */}
        <UserPacksCard
          packs={userPacks?.packs || []}
          loading={packsLoading}
          lang={lang} isEs={isEs} isPt={isPt}
          onConsume={(id, n) => consumeMut.mutate({ id, units: n })}
          consuming={consumeMut.isPending}
          onCancelRequest={(id) => cancelRequestMut.mutate(id)}
          cancellingRequest={cancelRequestMut.isPending}
        />

        {/* History */}
        <PaymentHistoryCard
          rows={history?.payments || []}
          loading={histLoading}
          T={T} lang={lang}
        />

        <MyGrantsCard isEs={isEs} />

        <p className="text-center text-xs text-slate-400 flex items-center justify-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
          {T.private}
        </p>
      </div>

      {/* Cancel confirm dialog — iter-168e: Save-the-Sale flow.
          Fetches win-back eligibility on open; if eligible, displays
          the discount offer as the primary path (rose CTA) and demotes
          the cancel action to a quiet secondary. This is *the* moment
          to retain — users typically don't scroll past dialog footer. */}
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent data-testid="my-plan-cancel-dialog">
          <SaveTheSaleContent
            T={T}
            isEs={isEs}
            cancel={cancel}
            onClose={() => setCancelOpen(false)}
          />
        </AlertDialogContent>
      </AlertDialog>
      <ContactSupportBlock lang={lang} />
    </Layout>
  );
}

/** Discreet contact CTA at the bottom of MyPlan. Hidden when the admin
 *  hasn't configured `support_contact_email` in /admin → Soporte. */
function ContactSupportBlock({ lang }) {
  const { data } = useSupportInfo();
  const email = data?.contact_email;
  if (!email) return null;
  const isEn = lang === "en";
  const isPt = lang === "pt";
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-10" data-testid="my-plan-contact-support">
      <div className="border-t border-cream-300 pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="text-sm text-slate-500">
          {isEn
            ? "Question about your billing, plan or a manual pack?"
            : isPt
            ? "Dúvida sobre cobrança, plano ou pack manual?"
            : "¿Dudas sobre tu cobro, plan o un pack manual?"}
        </div>
        <a
          href={`mailto:${email}?subject=${encodeURIComponent("Lumfi — " + (isEn ? "Billing inquiry" : isPt ? "Consulta de cobrança" : "Consulta de cobros"))}`}
          data-testid="my-plan-contact-link"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-300 bg-white text-sm text-slate-700 hover:border-emerald-500 hover:text-emerald-700 transition-colors"
        >
          <Mail className="h-3.5 w-3.5" />
          {isEn ? "Contact us" : isPt ? "Fale conosco" : "Cuentactanos"}
          <span className="text-xs text-slate-400">{email}</span>
        </a>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- */

/**
 * SaveTheSaleContent — body of the cancel confirm dialog (iter-168e).
 *
 * Fetches win-back eligibility lazily on mount; when eligible, promotes
 * the discount offer to the primary slot and demotes "confirm cancellation"
 * to a quiet outline button. UX rationale: the dialog is the highest-intent
 * moment to retain users.
 */
function SaveTheSaleContent({ T, isEs, cancel, onClose }) {
  const qc = useQueryClient();
  const { data: winback } = useQuery({
    queryKey: ["winback-eligibility-cancel-dialog"],
    queryFn: () => billingService.winbackEligibility(),
    staleTime: 30_000,
  });

  const claim = useMutation({
    mutationFn: () => billingService.winbackClaim(),
    onSuccess: (data) => {
      toast.success(
        isEs
          ? `¡Gracias por quedarte! ${data?.discount_pct}% off aplicado.`
          : `Thanks for staying! ${data?.discount_pct}% off applied.`,
      );
      qc.invalidateQueries({ queryKey: ["billing-me"] });
      onClose?.();
    },
    onError: () =>
      toast.error(isEs ? "No pudimos aplicar la oferta." : "Could not apply offer."),
  });

  const hasOffer = winback?.eligible;

  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle className="font-serif-display text-2xl">
          {hasOffer
            ? (isEs ? "Antes de irte…" : "Before you go…")
            : T.confirmCancel}
        </AlertDialogTitle>
        <AlertDialogDescription>
          {hasOffer ? null : T.confirmCancelD}
        </AlertDialogDescription>
      </AlertDialogHeader>

      {hasOffer ? (
        <div
          data-testid="save-the-sale-offer"
          className="rounded-xl border border-rose-200 bg-gradient-to-br from-rose-50 via-amber-50 to-white p-4 my-2 animate-in fade-in slide-in-from-bottom-2 duration-300"
        >
          <p className="text-sm text-rose-900 leading-relaxed">{winback.copy}</p>
        </div>
      ) : null}

      <AlertDialogFooter className="gap-2 flex-col-reverse sm:flex-row">
        {hasOffer ? (
          <>
            <AlertDialogCancel
              data-testid="save-the-sale-confirm-cancel"
              onClick={() => cancel.mutate()}
              disabled={cancel.isPending}
              className="border-slate-200 text-slate-500 hover:bg-slate-50"
            >
              {isEs ? "No gracias, cancela igual" : "No thanks, cancel anyway"}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="save-the-sale-accept"
              onClick={() => claim.mutate()}
              disabled={claim.isPending}
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600 gap-2"
            >
              {isEs
                ? `Quedarme con ${winback.discount_pct}% off`
                : `Stay with ${winback.discount_pct}% off`}
            </AlertDialogAction>
          </>
        ) : (
          <>
            <AlertDialogCancel data-testid="my-plan-cancel-keep">{T.keep}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="my-plan-cancel-confirm"
              onClick={() => cancel.mutate()}
              disabled={cancel.isPending}
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
            >
              {T.confirm}
            </AlertDialogAction>
          </>
        )}
      </AlertDialogFooter>
    </>
  );
}


function EmptyPlan({ T, isEs = true, recentlyCancelled = null }) {
  // iter-168e: differentiated empty state for users who cancelled in the
  // last 30d. Higher-conversion CTA + warmer copy. If they just churned
  // by reason "grace_period_expired" (involuntary) we show a softer
  // "update payment" hint instead of a sales push.
  if (recentlyCancelled) {
    const isInvoluntary = recentlyCancelled.cancellation_reason === "grace_period_expired";
    return (
      <div
        data-testid="my-plan-empty-recent-cancelled"
        className="rounded-3xl border border-rose-100 bg-gradient-to-br from-rose-50/40 via-amber-50/30 to-white p-10 lg:p-14 text-center animate-in fade-in duration-500"
      >
        <div className="mx-auto h-14 w-14 rounded-2xl bg-rose-100 text-rose-600 flex items-center justify-center mb-5">
          <ArrowRight className="h-7 w-7" strokeWidth={1.5} />
        </div>
        <h2 className="font-serif-display text-2xl sm:text-3xl font-medium text-slate-900">
          {isInvoluntary
            ? (isEs ? "Tu acceso quedó pausado" : "Your access is paused")
            : (isEs ? "¿Vuelves a Lumfi?" : "Coming back to Lumfi?")}
        </h2>
        <p className="text-slate-600 mt-2 max-w-md mx-auto">
          {isInvoluntary
            ? (isEs
              ? `Tu suscripción a ${recentlyCancelled.plan_name || "tu plan"} se canceló porque no pudimos cobrar. Reactiva en 2 clicks.`
              : `Your ${recentlyCancelled.plan_name || "subscription"} was cancelled because we couldn't charge. Reactivate in 2 clicks.`)
            : (isEs
              ? `Tu plan ${recentlyCancelled.plan_name || ""} sigue aquí esperándote. Te damos la bienvenida con todos los beneficios al instante.`
              : `Your ${recentlyCancelled.plan_name || ""} plan is waiting. Welcome back with all your benefits restored instantly.`)}
        </p>
        <Link to="/pricing" className="inline-block mt-6">
          <Button
            data-testid="my-plan-reactivate-btn"
            size="lg"
            className="bg-rose-600 hover:bg-rose-700 rounded-full gap-2"
          >
            {isEs ? "Reactivar mi cuenta" : "Reactivate my account"}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div
      data-testid="my-plan-empty"
      className="rounded-3xl border border-cream-300 bg-white p-10 lg:p-14 text-center"
    >
      <div className="mx-auto h-14 w-14 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center mb-5">
        <Package2 className="h-7 w-7" strokeWidth={1.5} />
      </div>
      <h2 className="font-serif-display text-2xl sm:text-3xl font-medium text-slate-900">{T.noPlan}</h2>
      <p className="text-slate-600 mt-2 max-w-md mx-auto">{T.noPlanSub}</p>
      <Link to="/pricing" className="inline-block mt-6">
        <Button data-testid="my-plan-see-plans-btn" size="lg" className="bg-emerald-600 hover:bg-emerald-700 rounded-full gap-2">
          {T.seePlans} <ArrowRight className="h-4 w-4" />
        </Button>
      </Link>
    </div>
  );
}

function ActivePlan({ comp, plan, sub, usage, status, T, lang, viaGrant = false, grantExpiresAt = null, onCancel, onCancelPending, cancelPendingPending, secondaryName = {} }) {
  const isEs = lang !== "en";
  const isPending = status === "pending";
  const secondaryIds = comp?.secondary_plan_ids || [];
  const periodEnd = sub?.current_period_end || comp?.current_period_end || grantExpiresAt;
  const periodStart = sub?.current_period_start || comp?.current_period_start;
  const daysLeft = usage?.days_remaining_in_period;
  // iter-169v — usage is feature-driven now. The right column lists
  // each metered feature (e.g. `image_extractions`) instead of a single
  // global "AI messages" counter. `pack_balance` survives as a small
  // summary for the assistant chat (where packs still represent the
  // top-up unit). The detailed per-pack view lives in <UserPacksCard>.
  const featureUsage = Array.isArray(usage?.feature_usage) ? usage.feature_usage : [];
  const packBalance = Number(usage?.pack_balance || 0);

  return (
    <div className="grid lg:grid-cols-3 gap-5 lg:gap-6" data-testid="my-plan-active">
      {/* Main plan card (2/3) */}
      <div className="lg:col-span-2 rounded-3xl border border-cream-300 bg-white p-7 lg:p-9 shadow-[0_18px_48px_-30px_rgba(15,23,42,0.18)]">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{T.plan}</div>
            <div className="font-serif-display text-3xl sm:text-4xl font-medium text-slate-900 mt-1 leading-tight flex items-center gap-2 flex-wrap">
              {plan?.name || "—"}
              {viaGrant && (
                <span
                  data-testid="my-plan-via-grant-badge"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-800 border border-violet-200 text-[10px] font-bold uppercase tracking-wider"
                  title={isEs
                    ? "Plan otorgado como bono por el equipo Lumfi."
                    : "Plan granted as a bonus by the Lumfi team."}
                >
                  🎁 {isEs ? "Bono" : "Bonus"}
                </span>
              )}
            </div>
          </div>
          <StatusBadge status={status} lang={lang} />
        </div>

        {/* Add-ons */}
        {secondaryIds.length > 0 && (
          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">{T.addons}</div>
            <div className="flex flex-wrap gap-2">
              {secondaryIds.map((id) => (
                <span key={id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cream-200 border border-cream-300 text-slate-700 text-xs font-medium" data-testid={`my-plan-addon-${id}`}>
                  <Layers className="h-3 w-3" />
                  {secondaryName[id] || `${id.slice(0, 8)}…`}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Period info */}
        <div className="mt-7 grid sm:grid-cols-2 gap-4">
          <div className="rounded-2xl bg-cream-100 border border-cream-300 p-5">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">
              <CalendarClock className="h-3.5 w-3.5" />
              {T.nextRenewal}
            </div>
            <div className="font-mono-num text-2xl font-medium text-slate-900 mt-1.5">{fmtDate(periodEnd, lang)}</div>
            {Number.isFinite(daysLeft) && (
              <div className="text-xs text-emerald-700 font-semibold mt-1">{daysLeft} {T.daysLeft}</div>
            )}
          </div>
          <div className="rounded-2xl bg-cream-100 border border-cream-300 p-5">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">
              <CreditCard className="h-3.5 w-3.5" />
              {isEs ? "Periodo actual" : "Current period"}
            </div>
            <div className="font-mono-num text-base font-medium text-slate-700 mt-1.5 leading-snug">
              {fmtDate(periodStart, lang)} <span className="text-slate-400">→</span> {fmtDate(periodEnd, lang)}
            </div>
          </div>
        </div>

        {/* Pending notice */}
        {isPending && (
          <div className="mt-6 p-4 rounded-2xl bg-amber-50 border border-amber-200 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900 leading-relaxed">
              <p className="font-semibold">{isEs ? "Tu compra está pendiente de confirmación." : "Your purchase is pending confirmation."}</p>
              <p className="mt-1">{isEs ? "Si pagaste, el procesador de pagos puede tardar unos minutos en confirmar. Si nunca completaste el checkout, puedes cancelarla."
                                       : "If you already paid, the payment processor can take a few minutes to confirm. Otherwise you can cancel it."}</p>
              <Button
                data-testid="my-plan-cancel-pending-btn"
                variant="outline" size="sm"
                onClick={onCancelPending}
                disabled={cancelPendingPending}
                className="mt-3 border-amber-300 text-amber-900 hover:bg-amber-100 gap-2"
              >
                <XCircle className="h-4 w-4" /> {T.cancelPending}
              </Button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-7 flex flex-wrap items-center gap-2">
          <Link to="/pricing">
            <Button data-testid="my-plan-change-btn" className="bg-emerald-600 hover:bg-emerald-700 rounded-full gap-2">
              {T.changePlan} <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
          <Link to="/pricing">
            <Button data-testid="my-plan-buy-packs-btn" variant="outline" className="rounded-full gap-2">
              <Package2 className="h-4 w-4" /> {T.buyPacks}
            </Button>
          </Link>
          {status === "active" && (
            <Button
              data-testid="my-plan-cancel-btn"
              variant="ghost"
              className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 rounded-full gap-2 ml-auto"
              onClick={onCancel}
            >
              <XCircle className="h-4 w-4" /> {T.cancelSub}
            </Button>
          )}
        </div>
      </div>

      {/* Usage card (1/3) — iter-169v feature-driven (replaces global credits).
          Top: per-feature metered usage (used / allotment + progress + buy CTA).
          Bottom: ALWAYS-visible pack balance summary + upgrade CTA — this is
          the main upsell surface for the assistant top-ups, do not hide it. */}
      <div className="rounded-3xl border border-cream-300 bg-white p-7 lg:p-8" data-testid="my-plan-usage-card">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">
          <Sparkles className="h-3 w-3 text-emerald-600" />
          {T.usageTitle}
        </div>

        {featureUsage.length > 0 ? (
          <div className="mt-4 space-y-5">
            {featureUsage.map((it) => {
              const allot = Math.max(0, Number(it.allotment) || 0);
              const u = Math.max(0, Number(it.used) || 0);
              const remaining = Math.max(0, Number(it.remaining ?? (allot - u)));
              const pct = allot > 0 ? Math.min(100, Math.round((u / allot) * 100)) : 0;
              const low = allot > 0 && remaining / allot < 0.2;
              const barColor = pct >= 85 ? "bg-rose-500" : pct >= 60 ? "bg-amber-500" : "bg-emerald-500";
              return (
                <div key={it.feature_key} data-testid={`my-plan-feature-${it.feature_key}`}>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-sm font-medium text-slate-800">{featureLabel(it.feature_key, lang)}</span>
                    <span className="text-xs text-slate-500 tabular-nums">
                      <span className="text-slate-900 font-semibold font-mono-num">{u.toLocaleString()}</span>
                      {" / "}{allot.toLocaleString()}
                    </span>
                  </div>
                  <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className={`h-full ${barColor}`} style={{ width: `${Math.max(2, pct)}%` }} />
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className={`text-[11px] ${low ? "text-rose-600 font-semibold" : "text-slate-500"}`}>
                      {isEs ? `Te quedan ${remaining.toLocaleString()}` : `${remaining.toLocaleString()} remaining`}
                    </span>
                    {low && (
                      <Link to="/pricing">
                        <Button
                          size="sm" variant="outline"
                          className="h-7 text-[11px] gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          data-testid={`my-plan-feature-buy-${it.feature_key}`}
                        >
                          <ShoppingCart className="h-3 w-3" />
                          {T.buyMore}
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-3 text-sm text-slate-500">{T.usageEmpty}</div>
        )}

        {/* Pack balance — ALWAYS visible. Even at 0, the "Comprar packs" CTA
            here is one of the main upgrade nudges in the app. */}
        <div className="mt-6 pt-6 border-t border-cream-300" data-testid="my-plan-pack-balance">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 flex items-center gap-1.5">
            <Package2 className="h-3 w-3" />
            {T.packBalance}
          </div>
          <div className="font-mono-num text-3xl font-medium text-emerald-700 mt-2 glow-emerald">{packBalance}</div>
          <p className="text-xs text-slate-500 mt-1.5 leading-snug">{T.packBalanceSub}</p>
          <Link to="/pricing" className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 mt-3 hover:text-emerald-800" data-testid="my-plan-buy-packs-inline">
            <Package2 className="h-3 w-3" /> {T.buyPacks} <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function PaymentHistoryCard({ rows, loading, T, lang }) {
  return (
    <div className="rounded-3xl border border-cream-300 bg-white overflow-hidden" data-testid="my-plan-payments-card">
      <div className="px-7 lg:px-9 py-5 border-b border-cream-300 flex items-center gap-2">
        <Receipt className="h-4 w-4 text-slate-500" />
        <h3 className="font-serif-display text-xl font-medium text-slate-900">{T.paymentsHist}</h3>
        <span className="ml-auto text-xs text-slate-400 font-mono-num">{rows.length}</span>
      </div>
      {loading ? (
        <div className="p-10 text-center text-slate-400">…</div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-center text-slate-400 flex flex-col items-center gap-2" data-testid="my-plan-payments-empty">
          <Receipt className="h-8 w-8 text-slate-300" strokeWidth={1.5} />
          <span>{T.noPayments}</span>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cream-100 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">{T.when}</th>
                <th className="text-left px-5 py-3 font-semibold">{T.concept}</th>
                <th className="text-right px-5 py-3 font-semibold">{T.amount}</th>
                <th className="text-left px-5 py-3 font-semibold hidden md:table-cell">{T.mpId}</th>
                <th className="text-center px-5 py-3 font-semibold">{T.status}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const concept = p.type === "subscription"
                  ? `${T.type_sub} · ${p.plan_name || p.plan_id?.slice(0,8) || "—"}`
                  : `${T.type_pack} · ${p.pack_name || p.pack_id?.slice(0,8) || "—"}`;
                const mpId = p.mp_order_id || p.mp_payment_id || p.mp_preference_id;
                return (
                  <tr key={p.id} className="border-t border-cream-300" data-testid={`my-plan-payment-${p.id}`}>
                    <td className="px-5 py-3 text-xs text-slate-600 whitespace-nowrap">{fmtDateTime(p.created_at, lang)}</td>
                    <td className="px-5 py-3 text-sm text-slate-800 max-w-[280px] truncate" title={concept}>
                      <span className="inline-flex items-center gap-2">
                        {p.type === "subscription"
                          ? <CreditCard className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                          : <Package2 className="h-3.5 w-3.5 text-slate-400 shrink-0" />}
                        {concept}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right font-mono-num text-sm tabular-nums whitespace-nowrap">
                      {p.currency ? formatMoney(p.amount, p.currency) : p.amount}
                    </td>
                    <td className="px-5 py-3 text-xs font-mono text-slate-500 hidden md:table-cell">
                      {mpId ? (
                        <button /* design-system: ALLOW legacy-todo */
                          onClick={() => {
                            navigator.clipboard?.writeText(mpId);
                            toast.success(lang !== "en" ? "Copiado" : "Copied");
                          }}
                          className="hover:text-slate-900 truncate max-w-[160px] inline-block align-middle"
                          title={mpId}
                          data-testid={`my-plan-mp-id-${p.id}`}
                        >
                          {mpId.length > 18 ? `${mpId.slice(0, 18)}…` : mpId}
                        </button>
                      ) : "—"}
                    </td>
                    <td className="px-5 py-3 text-center">
                      <StatusBadge status={p.status} lang={lang} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- */
/* Active packs card (consumables)                                   */
/* ----------------------------------------------------------------- */
function UserPacksCard({ packs, loading, lang, isEs, isPt, onConsume, consuming, onCancelRequest, cancellingRequest }) {
  const title = isPt ? "Meus packs" : isEs ? "Mis packs" : "My packs";
  const empty = isPt ? "Você ainda não comprou nenhum pack."
              : isEs ? "Aún no compraste ningún pack."
              : "You haven't purchased any pack yet.";
  const seePacks = isPt ? "Ver packs disponíveis" : isEs ? "Ver packs disponibles" : "Browse available packs";
  const unitsLabel = isEs ? "unidades restantes" : "units remaining";
  const daysLabel = isEs ? "días restantes" : "days remaining";
  const unlimitedUnits = isEs ? "Uso ilimitado por tiempo" : "Unlimited use (time only)";
  const unlimitedTime = isEs ? "Sin vencimiento" : "No expiry";

  if (loading) {
    return (
      <div className="rounded-3xl border border-cream-300 bg-white p-10 text-center text-slate-400" data-testid="my-plan-packs-loading">…</div>
    );
  }

  // Show active first, then exhausted/expired below in a faded section.
  const active = packs.filter((p) => p.status === "active");
  const inactive = packs.filter((p) => p.status !== "active");

  return (
    <div className="rounded-3xl border border-cream-300 bg-white overflow-hidden" data-testid="my-plan-packs-card">
      <div className="px-7 lg:px-9 py-5 border-b border-cream-300 flex items-center gap-2">
        <Package2 className="h-4 w-4 text-slate-500" />
        <h3 className="font-serif-display text-xl font-medium text-slate-900">{title}</h3>
        <span className="ml-auto text-xs text-slate-400 font-mono-num">{active.length}</span>
      </div>

      {packs.length === 0 ? (
        <div className="p-10 text-center text-slate-400 flex flex-col items-center gap-3" data-testid="my-plan-packs-empty">
          <Package2 className="h-8 w-8 text-slate-300" strokeWidth={1.5} />
          <span>{empty}</span>
          <Link to="/pricing">
            <Button variant="outline" size="sm" className="mt-2 gap-1" data-testid="my-plan-packs-empty-cta">
              {seePacks} <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </Link>
        </div>
      ) : (
        <div className="p-5 lg:p-7 space-y-4">
          {/* iter-195 — group identical active packs (same pack_id) so
              the user sees "5 unidades activas" instead of 5 visually
              identical rows. Each group expands to show individual
              packs for actions like consume / cancel. */}
          {(() => {
            const groups = {};
            active.forEach((p) => {
              const key = p.pack_id || p.pack_name || p.id;
              if (!groups[key]) groups[key] = [];
              groups[key].push(p);
            });
            return Object.entries(groups).map(([gk, list]) => (
              list.length > 1 ? (
                <UserPackGroup
                  key={gk}
                  groupKey={gk}
                  packs={list}
                  lang={lang} isEs={isEs} isPt={isPt}
                  unitsLabel={unitsLabel} daysLabel={daysLabel}
                  unlimitedUnits={unlimitedUnits} unlimitedTime={unlimitedTime}
                  onConsume={onConsume} consuming={consuming}
                  onCancelRequest={onCancelRequest} cancellingRequest={cancellingRequest}
                />
              ) : (
                <UserPackRow
                  key={list[0].id} pack={list[0]} lang={lang} isEs={isEs} isPt={isPt}
                  unitsLabel={unitsLabel} daysLabel={daysLabel}
                  unlimitedUnits={unlimitedUnits} unlimitedTime={unlimitedTime}
                  onConsume={onConsume} consuming={consuming}
                  onCancelRequest={onCancelRequest} cancellingRequest={cancellingRequest}
                />
              )
            ));
          })()}
          {inactive.length > 0 && (
            <details className="pt-2 border-t border-cream-300">
              <summary className="text-xs font-semibold text-slate-500 cursor-pointer hover:text-slate-700 pt-3">
                {isEs ? `Packs anteriores (${inactive.length})` : `Past packs (${inactive.length})`}
              </summary>
              <div className="mt-3 space-y-2 opacity-70">
                {inactive.map((p) => (
                  <UserPackRow key={p.id} pack={p} lang={lang} isEs={isEs} unitsLabel={unitsLabel} daysLabel={daysLabel} unlimitedUnits={unlimitedUnits} unlimitedTime={unlimitedTime} dimmed />
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

// iter-195 — Collapsible group of identical active packs. Shows the
// aggregated unit count up front (e.g. "Pack X · 12 unidades activas")
// and expands into individual rows on click so the user can still
// consume / cancel each one.
function UserPackGroup({
  groupKey, packs, lang, isEs, isPt,
  unitsLabel, daysLabel, unlimitedUnits, unlimitedTime,
  onConsume, consuming, onCancelRequest, cancellingRequest,
}) {
  const [open, setOpen] = useState(false);
  const sample = packs[0];
  const totalUnits = packs.reduce((s, p) => s + Number(p.units_remaining || 0), 0);
  const initialUnits = packs.reduce((s, p) => s + Number(p.units_initial || 0), 0);
  const unlimitedAny = packs.some((p) => Number(p.units_initial || 0) === 0);
  const ratio = initialUnits > 0 ? Math.min(100, Math.round((totalUnits / initialUnits) * 100)) : 0;

  return (
    <div
      className="rounded-2xl border border-cream-300 bg-cream-100"
      data-testid={`my-plan-pack-group-${groupKey}`}
    >
      <button /* design-system: ALLOW legacy-todo */
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left p-5 lg:p-6 flex items-center gap-3 hover:bg-stone-50/40 rounded-2xl transition-colors"
        data-testid={`my-plan-pack-group-${groupKey}-toggle`}
      >
        <div className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
          <Package2 className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-serif-display text-lg font-medium text-slate-900">{sample.pack_name || "Pack"}</span>
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200">
              ×{packs.length} {isEs ? "activas" : "active"}
            </span>
          </div>
          <div className="mt-1 text-xs text-slate-600 tabular-nums">
            {unlimitedAny ? (
              <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                <Infinity className="h-3 w-3" /> {unlimitedUnits}
              </span>
            ) : (
              <>
                <strong>{totalUnits}</strong> / {initialUnits} {unitsLabel} ·{" "}
                <span className={ratio < 30 ? "text-amber-700" : "text-emerald-700"}>
                  {ratio}%
                </span>
              </>
            )}
          </div>
          {!unlimitedAny && initialUnits > 0 && (
            <div className="mt-1.5 h-1.5 rounded-full bg-stone-200 overflow-hidden max-w-md">
              <div className={`h-full ${ratio < 30 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${ratio}%` }} />
            </div>
          )}
        </div>
        <ArrowRight className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 lg:px-6 lg:pb-6 space-y-2 border-t border-cream-300 pt-3">
          {packs.map((p) => (
            <UserPackRow
              key={p.id} pack={p} lang={lang} isEs={isEs} isPt={isPt}
              unitsLabel={unitsLabel} daysLabel={daysLabel}
              unlimitedUnits={unlimitedUnits} unlimitedTime={unlimitedTime}
              onConsume={onConsume} consuming={consuming}
              onCancelRequest={onCancelRequest} cancellingRequest={cancellingRequest}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function UserPackRow({ pack, lang, isEs, isPt, unitsLabel, daysLabel, unlimitedUnits, unlimitedTime, dimmed, onConsume, consuming, onCancelRequest, cancellingRequest }) {
  const unitsInit = Number(pack.units_initial || 0);
  const unitsLeft = Number(pack.units_remaining || 0);
  const days = pack.days_remaining;
  const unitPct = unitsInit > 0 ? Math.max(0, Math.min(100, Math.round((unitsLeft / unitsInit) * 100))) : 0;
  const statusLabel = STATUS_LABEL[pack.status] || STATUS_LABEL.active;
  const isManual = (pack.fulfillment_mode || "automatic") === "manual";
  // iter-170k — manual packs lock the "Solicitar" button while the
  // request is pending soporte resolution.
  const requestPending = isManual && pack.consumption_request_status === "pending";
  const canConsume = !dimmed && !requestPending && pack.status === "active" && (unitsInit === 0 || unitsLeft > 0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [qty, setQty] = useState(1);

  // Reset qty to 1 on open + keep within bounds.
  const openConfirm = () => { setQty(1); setConfirmOpen(true); };
  const maxQty = unitsInit > 0 ? unitsLeft : 99;

  return (
    <div
      data-testid={`my-plan-pack-${pack.id}`}
      className={`rounded-2xl border ${dimmed ? "border-stone-200 bg-stone-50/40" : "border-cream-300 bg-cream-100"} p-5 lg:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-serif-display text-xl font-medium text-slate-900 leading-snug">{pack.pack_name || "Pack"}</span>
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${statusLabel.cls}`}>
            {statusLabel[lang] || statusLabel.es}
          </span>
          {isManual && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200" data-testid={`my-plan-pack-${pack.id}-mode-manual`}>
              {isPt ? "Manual" : isEs ? "Manual" : "Manual"}
            </span>
          )}
          {requestPending && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-50 text-sky-700 border border-sky-200"
              data-testid={`my-plan-pack-${pack.id}-request-pending`}
            >
              <Hourglass className="h-3 w-3" />
              {isPt
                ? "Aguardando suporte"
                : isEs
                ? "Esperando respuesta de soporte"
                : "Waiting for support"}
            </span>
          )}
        </div>
        {pack.description_note && (
          <p className="text-sm text-slate-600 mt-1 leading-snug">{pack.description_note}</p>
        )}
        <p className="text-[10px] text-slate-400 mt-1.5">
          {isEs ? "Comprado" : "Purchased"} · {fmtDate(pack.purchased_at, lang)}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 md:gap-6 md:w-[260px] shrink-0">
        {/* Units */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">
            {isEs ? "Unidades" : "Units"}
          </div>
          {unitsInit > 0 ? (
            <>
              <div className="flex items-baseline gap-1.5 mt-1">
                <span className="font-mono-num text-2xl font-medium text-slate-900">{unitsLeft}</span>
                <span className="text-xs text-slate-500">/ {unitsInit}</span>
              </div>
              <div className="mt-1.5 h-1.5 rounded-full bg-stone-200 overflow-hidden">
                <div className={`h-full ${unitPct < 30 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${unitPct}%` }} />
              </div>
              <div className="text-[10px] text-slate-400 mt-1">{unitsLabel}</div>
            </>
          ) : (
            <div className="flex items-center gap-1 mt-1.5 text-emerald-700 text-xs font-semibold">
              <Infinity className="h-3.5 w-3.5" /> {unlimitedUnits}
            </div>
          )}
        </div>

        {/* Days */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">
            {isEs ? "Vencimiento" : "Validity"}
          </div>
          {pack.valid_for_days && pack.valid_for_days > 0 ? (
            <>
              <div className="flex items-baseline gap-1.5 mt-1">
                <span className="font-mono-num text-2xl font-medium text-slate-900">{days ?? "—"}</span>
                <span className="text-xs text-slate-500">{isEs ? "d" : "d"}</span>
              </div>
              <div className="text-[10px] text-slate-400 mt-1">{daysLabel}</div>
              {pack.expires_at && (
                <div className="text-[10px] text-slate-400 mt-0.5">
                  <Hourglass className="h-2.5 w-2.5 inline mr-0.5" />
                  {fmtDate(pack.expires_at, lang)}
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center gap-1 mt-1.5 text-emerald-700 text-xs font-semibold">
              <Infinity className="h-3.5 w-3.5" /> {unlimitedTime}
            </div>
          )}
        </div>
      </div>

      {/* Use button */}
      {canConsume && onConsume && (
        <div className="shrink-0 md:ml-2">
          <Button
            data-testid={`my-plan-pack-${pack.id}-use-btn`}
            onClick={openConfirm}
            disabled={consuming}
            className="bg-emerald-600 hover:bg-emerald-700 rounded-full px-5 gap-1.5"
          >
            <Play className="h-4 w-4" />
            {isManual
              ? (isPt ? "Solicitar" : isEs ? "Solicitar" : "Request")
              : (isPt ? "Usar" : isEs ? "Usar" : "Use")}
          </Button>
        </div>
      )}
      {requestPending && (
        <div className="shrink-0 md:ml-2 flex flex-col items-end gap-1.5" data-testid={`my-plan-pack-${pack.id}-pending-msg`}>
          <div className="text-[11px] text-slate-500 italic text-right max-w-[200px]">
            {isPt
              ? "Te avisaremos por email assim que o suporte processar."
              : isEs
              ? "Te avisaremos por email cuando soporte la procese."
              : "We'll email you once support processes it."}
          </div>
          {onCancelRequest && (
            <Button
              variant="outline"
              size="sm"
              disabled={cancellingRequest}
              onClick={() => onCancelRequest(pack.id)}
              data-testid={`my-plan-pack-${pack.id}-cancel-request-btn`}
              className="text-[11px] h-7 px-3 rounded-full border-slate-300 text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            >
              {isPt ? "Cancelar minha solicitação"
                : isEs ? "Cancelar mi solicitud"
                : "Cancel my request"}
            </Button>
          )}
        </div>
      )}

      {/* Confirm dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid={`my-plan-pack-${pack.id}-confirm-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-2xl">
              {isEs ? "Usar este pack" : "Use this pack"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isManual
                ? (isEs ? "Al confirmar, descontamos las unidades y enviamos tu solicitud al equipo de Lumfi para que te contacte y ejecute el trabajo."
                        : "On confirm we'll debit the units and notify the Lumfi team to reach out and execute the work.")
                : (isEs ? "Las unidades se descuentan inmediatamente. Si todavía no hay una feature de la app conectada a este pack, igual sirve como registro."
                        : "Units are debited immediately. If no app feature is wired to this pack yet, it still acts as a record of use.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {unitsInit > 0 && (
            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">
                {isEs ? "Cantidad" : "Quantity"}
              </label>
              <div className="flex items-center gap-2">
                <Button
                  type="button" variant="outline" size="icon"
                  className="h-9 w-9 rounded-full"
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  data-testid={`my-plan-pack-${pack.id}-qty-minus`}
                >−</Button>
                <input /* design-system: ALLOW legacy-todo */
                  type="number"
                  min={1}
                  max={maxQty}
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, Math.min(maxQty, Number(e.target.value) || 1)))}
                  className="w-20 text-center font-mono-num text-xl font-medium border border-cream-300 rounded-xl py-2 focus:outline-none focus:border-emerald-500"
                  data-testid={`my-plan-pack-${pack.id}-qty-input`}
                />
                <Button
                  type="button" variant="outline" size="icon"
                  className="h-9 w-9 rounded-full"
                  onClick={() => setQty(Math.min(maxQty, qty + 1))}
                  data-testid={`my-plan-pack-${pack.id}-qty-plus`}
                >+</Button>
                <span className="text-xs text-slate-500 ml-auto">/ {unitsLeft} {isEs ? "disponibles" : "available"}</span>
              </div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`my-plan-pack-${pack.id}-confirm-cancel`}>
              {isEs ? "Cancelar" : "Cancel"}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid={`my-plan-pack-${pack.id}-confirm-use`}
              onClick={() => { onConsume(pack.id, qty); setConfirmOpen(false); }}
              disabled={consuming}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {isManual
                ? (isEs ? "Solicitar al equipo" : "Send to team")
                : (isEs ? "Confirmar" : "Confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
